/**
 * Router utility functions that can be imported without creating circular dependencies.
 *
 * IMPORTANT: This file must NOT import from router.tsx or routeTree.gen to avoid cycles.
 * The updateRouterContext function lazily accesses the router instance.
 */

import { getRouterContext } from './route-guards';

let routerInstance: {
  update: (options: { context: ReturnType<typeof getRouterContext> }) => void;
  preloadRoute: (opts: { to: string }) => Promise<unknown>;
} | null = null;

/**
 * Register the router instance. Called once during app initialization.
 */
export function registerRouter(
  router: NonNullable<typeof routerInstance>
): void {
  routerInstance = router;
}

/**
 * Update the router context with fresh auth state.
 * Use this after login/logout to refresh route guards.
 */
export function updateRouterContext(): void {
  if (!routerInstance) {
    return;
  }
  routerInstance.update({
    context: getRouterContext(),
  });
}

/**
 * Preload a route before navigating.
 * Use this to avoid layout shift when navigating to heavy routes.
 */
export async function preloadRoute(opts: { to: string }): Promise<void> {
  if (!routerInstance) {
    return;
  }
  await routerInstance.preloadRoute(opts);
}
